import React, { useEffect, useState } from "react";
import { Loader2, Network, Users, Award } from "lucide-react";
import { StatCard, money } from "./DashWidgets";
import { teamTree, rankMine } from "../lib/api";

const LEVEL_COLORS = ["#D4AF37", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"];

function flatten(nodes, out) {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    out.push(n);
    if (n.children && n.children.length) flatten(n.children, out);
  }
}

export const TeamTree = () => {
  const [data, setData] = useState(null);
  const [rank, setRank] = useState(null);
  useEffect(() => {
    teamTree()
      .then(setData)
      .catch(() => setData({ tree: [], levels: [], total_members: 0, total_volume: 0 }));
    rankMine().then(setRank).catch(() => {});
  }, []);

  if (!data) return <Loader2 className="w-6 h-6 text-gold animate-spin" />;

  const rows = [];
  flatten(data.tree || [], rows);

  return (
    <div data-testid="team-tree">
      <h2 className="font-heading text-2xl font-bold text-white mb-5 flex items-center gap-2">
        <Network className="w-6 h-6 text-gold" /> My Team Tree
      </h2>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon="Users" label="Total Downline" value={data.total_members} testid="team-total-members" />
        <StatCard icon="Layers" label="Levels Deep" value={(data.levels || []).length} testid="team-levels" />
        <StatCard icon="Banknote" label="Team Business Volume" value={money(data.total_volume)} accent="green" testid="team-volume" />
      </div>

      {rank && rank.rank && (
        <div className="card-dark rounded-2xl p-5 mb-6" data-testid="my-rank-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: (rank.rank.color || "#D4AF37") + "22" }}>
                <Award className="w-6 h-6" style={{ color: rank.rank.color || "#D4AF37" }} />
              </span>
              <div>
                <div className="text-slate-400 text-xs uppercase tracking-widest">Current Rank</div>
                <div className="text-white font-heading text-xl font-extrabold">{rank.rank.name}</div>
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="text-slate-300">Personal <span className="font-mono-fin text-white">{money(rank.personal)}</span> · Team <span className="font-mono-fin text-white">{money(rank.team)}</span> · Directs <span className="text-white">{rank.directs}</span></div>
              {rank.next && (() => {
                const gaps = [];
                const pv = rank.next.min_personal - rank.personal; if (pv > 0) gaps.push(`PV +${money(pv)}`);
                const tv = rank.next.min_team - rank.team; if (tv > 0) gaps.push(`TV +${money(tv)}`);
                const dr = rank.next.min_directs - rank.directs; if (dr > 0) gaps.push(`${dr} more direct${dr > 1 ? "s" : ""}`);
                return <div className="text-gold text-xs mt-1">Next: {rank.next.name}{gaps.length ? ` — need ${gaps.join(", ")}` : " — all criteria met, recompute to promote"}</div>;
              })()}
            </div>
          </div>
        </div>
      )}

      {(data.levels || []).length > 0 && (
        <div className="card-dark rounded-2xl p-5 mb-6">
          <div className="text-xs uppercase tracking-widest text-gold font-semibold mb-3">Level-wise Breakdown</div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5].map((lv) => {
              const l = (data.levels || []).find((x) => x.level === lv) || { count: 0, volume: 0 };
              const color = LEVEL_COLORS[(lv - 1) % LEVEL_COLORS.length];
              return (
                <div key={lv} className="bg-obsidian-900/60 rounded-xl p-3" data-testid={"team-level-" + lv}>
                  <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: color }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} /> Level {lv}
                  </div>
                  <div className="text-white font-bold mt-1 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 text-slate-500" /> {l.count}
                  </div>
                  <div className="font-mono-fin text-gold text-xs mt-0.5">{money(l.volume)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card-dark rounded-2xl p-4">
        <div className="text-xs uppercase tracking-widest text-gold font-semibold mb-3">Downline (up to 5 levels)</div>
        {rows.length === 0 ? (
          <p className="text-slate-400 text-sm py-4">No team members yet. Share your referral code to start building your downline.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((n) => {
              const color = LEVEL_COLORS[(n.level - 1) % LEVEL_COLORS.length];
              return (
                <div
                  key={n.id}
                  className="flex items-center justify-between gap-3 bg-obsidian-900/60 rounded-lg px-3 py-2"
                  style={{ marginLeft: (n.level - 1) * 20 }}
                  data-testid={"tree-node-" + n.id}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <div className="min-w-0">
                      <div className="text-white text-sm font-semibold truncate">
                        {n.name}
                        <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{n.role}</span>
                        {n.rank_name && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (n.rank_color || "#D4AF37") + "22", color: n.rank_color || "#D4AF37" }}>{n.rank_name}</span>}
                      </div>
                      <div className="text-slate-500 text-xs truncate">L{n.level} · {n.email || n.referral_code}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-fin text-gold text-sm">{money(n.volume)}</div>
                    <div className="text-slate-600 text-[10px]">volume</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
